import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiBaseUrl } from '../config/env';
import { useAuth } from './AuthContext';

interface ChatUser {
  id: string;
  name: string;
  photo?: string;
  profession?: string;
}

interface LastMessage {
  id: string;
  content: string;
  type: string;
  senderId: string;
  senderName: string;
  createdAt: string;
}

interface Conversation {
  id: string;
  type: string;
  name?: string;
  participants: ChatUser[];
  lastMessage: LastMessage | null;
  unreadCount: number;
  updatedAt: string;
  clubId?: string;
  club?: {
    id: string;
    name: string;
    chatEnabled: boolean;
    memberCount: number;
  } | null;
  memberCount?: number;
}

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  sender: ChatUser;
  content: string;
  type: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  createdAt: string;
  updatedAt: string;
}

interface ChatContextType {
  conversations: Conversation[];
  currentMessages: Message[];
  loading: boolean;
  totalUnreadChats: number;
  fetchConversations: (offset?: number) => Promise<void>;
  fetchMessages: (conversationId: string, offset?: number) => Promise<{ isBlocked: boolean; isBlockedBy: boolean }>;
  sendMessage: (conversationId: string, content: string, type?: string, fileUrl?: string, fileName?: string, fileSize?: number) => Promise<void>;
  createConversation: (participantId: string) => Promise<Conversation>;
  markAsRead: (conversationId: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
  refreshMessages: (conversationId: string) => Promise<void>;
  clearMessages: () => void;
  getClubConversation: (clubId: string) => Promise<Conversation>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalUnreadChats, setTotalUnreadChats] = useState(0);
  const { user } = useAuth();

  const getAuthToken = async () => {
    const token = await AsyncStorage.getItem('@lnb_token');
    return token;
  };

  // Poll for unread chat count every 30 seconds
  useEffect(() => {
    let interval: NodeJS.Timeout;

    const updateUnreadCount = () => {
      // Recalculate from current conversations
      const unreadCount = conversations.reduce((total, conv) => {
        return total + (conv.unreadCount > 0 ? 1 : 0);
      }, 0);
      setTotalUnreadChats(unreadCount);
    };

    const pollUnreadCount = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const response = await fetch(
          `${apiBaseUrl}/api/chats/conversations?limit=100&offset=0`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const unreadCount = (data.conversations || []).reduce((total: number, conv: Conversation) => {
            return total + (conv.unreadCount > 0 ? 1 : 0);
          }, 0);
          setTotalUnreadChats(unreadCount);
        }
      } catch (error) {
        console.error('Error polling chat unread count:', error);
      }
    };

    // Initial count
    updateUnreadCount();

    // Poll every 30 seconds
    if (user) {
      interval = setInterval(pollUnreadCount, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [conversations, user]);

  const fetchConversations = async (offset = 0) => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      
      console.log('🔍 Fetching conversations from:', `${apiBaseUrl}/api/chats/conversations`);
      
      const response = await fetch(
        `${apiBaseUrl}/api/chats/conversations?limit=20&offset=${offset}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', response.status, errorText);
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Conversations loaded:', data.conversations?.length || 0);
      
      console.log('📥 Received from backend (desc order):', data.conversations?.map((c: Conversation) => ({
        id: c.id,
        name: c.participants[0]?.name || c.name,
        updatedAt: c.updatedAt
      })));
      
      // Sort descending so newest conversations appear at the top
      const sortedConversations = (data.conversations || []).sort((a: Conversation, b: Conversation) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      
      console.log('🔄 After sorting desc (newest at top):', sortedConversations.map((c: Conversation) => ({
        id: c.id,
        name: c.participants[0]?.name || c.name,
        updatedAt: c.updatedAt
      })));
      
      if (offset === 0) {
        setConversations(sortedConversations);
      } else {
        setConversations((prev) => [...prev, ...sortedConversations]);
      }
      
      // Calculate total unread chats
      const unreadCount = sortedConversations.reduce((total: number, conv: Conversation) => {
        return total + (conv.unreadCount > 0 ? 1 : 0);
      }, 0);
      setTotalUnreadChats(unreadCount);
      console.log('💬 Total unread chats:', unreadCount);
    } catch (error: any) {
      console.error('Error fetching conversations:', error.message || error);
      // Set empty array on error to show empty state instead of crashing
      if (offset === 0) {
        setConversations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId: string, offset = 0) => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      
      console.log('💬 Fetching messages for conversation:', conversationId);
      
      const response = await fetch(
        `${apiBaseUrl}/api/chats/conversations/${conversationId}/messages?limit=50&offset=${offset}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Messages API Error:', response.status, errorText);
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Messages loaded:', data.messages?.length || 0);
      console.log('🚫 Blocked status:', { isBlocked: data.isBlocked, isBlockedBy: data.isBlockedBy });
      
      if (data.messages?.length > 0) {
        console.log('📥 First message (newest):', {
          id: data.messages[0].id,
          content: data.messages[0].content?.substring(0, 20),
          createdAt: data.messages[0].createdAt
        });
        console.log('📥 Last message (oldest):', {
          id: data.messages[data.messages.length - 1].id,
          content: data.messages[data.messages.length - 1].content?.substring(0, 20),
          createdAt: data.messages[data.messages.length - 1].createdAt
        });
      }
      
      if (offset === 0) {
        setCurrentMessages(data.messages || []);
      } else {
        setCurrentMessages((prev) => [...(data.messages || []), ...prev]);
      }
      
      return {
        isBlocked: data.isBlocked || false,
        isBlockedBy: data.isBlockedBy || false,
      };
    } catch (error: any) {
      console.error('Error fetching messages:', error.message || error);
      if (offset === 0) {
        setCurrentMessages([]);
      }
      return { isBlocked: false, isBlockedBy: false };
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (conversationId: string, content: string, type = 'text', fileUrl?: string, fileName?: string, fileSize?: number) => {
    // Create optimistic message for instant UI feedback
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      conversationId,
      senderId: user?.userId || '',
      sender: {
        id: user?.userId || '',
        name: user?.name || '',
        photo: user?.photo,
      },
      content,
      type,
      fileUrl,
      fileName,
      fileSize,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Immediately show message in UI
    setCurrentMessages((prev) => [optimisticMessage, ...prev]);

    try {
      const token = await getAuthToken();
      
      const response = await fetch(
        `${apiBaseUrl}/api/chats/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            content, 
            type,
            fileUrl,
            fileName,
            fileSize
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const message = await response.json();
      
      console.log('📤 Message sent:', {
        conversationId,
        messageId: message.id,
        createdAt: message.createdAt
      });
      
      // Replace optimistic message with real message
      setCurrentMessages((prev) => {
        console.log('💬 Before replacing optimistic message - count:', prev.length);
        const updated = [message, ...prev.filter(m => m.id !== optimisticMessage.id)];
        console.log('💬 After replacing optimistic message - count:', updated.length);
        return updated;
      });
      
      // Update conversation's last message and resort to keep most recent at end (bottom)
      setConversations((prev) => {
        console.log('🔄 Before update - conversations:', prev.map(c => ({
          id: c.id,
          name: c.participants[0]?.name || c.name,
          updatedAt: c.updatedAt
        })));
        
        const updatedConvs = prev.map((conv) =>
          conv.id === conversationId
            ? {
                ...conv,
                lastMessage: {
                  id: message.id,
                  content: message.content,
                  type: message.type,
                  senderId: message.senderId,
                  senderName: message.sender.name,
                  createdAt: message.createdAt,
                },
                updatedAt: message.createdAt,
              }
            : conv
        );
        
        console.log('🔄 After mapping - conversations:', updatedConvs.map(c => ({
          id: c.id,
          name: c.participants[0]?.name || c.name,
          updatedAt: c.updatedAt
        })));
        
        // Sort descending so newest conversation appears at the top
        const sorted = updatedConvs.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        console.log('✅ After sorting - conversations:', sorted.map(c => ({
          id: c.id,
          name: c.participants[0]?.name || c.name,
          updatedAt: c.updatedAt
        })));
        
        return sorted;
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message on error
      setCurrentMessages((prev) => prev.filter(m => m.id !== optimisticMessage.id));
      throw error;
    }
  };

  const createConversation = async (participantId: string): Promise<Conversation> => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${apiBaseUrl}/api/chats/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ participantId, type: 'personal' }),
      });

      if (!response.ok) {
        throw new Error('Failed to create conversation');
      }

      const conversation = await response.json();
      
      // Add to conversations if it's new and resort
      const exists = conversations.find((c) => c.id === conversation.id);
      if (!exists) {
        setConversations((prev) => {
          const updated = [...prev, conversation];
          // Sort descending so newest conversation appears at the top
          return updated.sort((a, b) => 
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        });
      }
      
      return conversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  const getClubConversation = async (clubId: string): Promise<Conversation> => {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${apiBaseUrl}/api/chats/clubs/${clubId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load club conversation');
      }

      const data = await response.json();
      const conversation: Conversation = data.conversation;

      setConversations((prev) => {
        const existing = prev.some((conv) => conv.id === conversation.id);
        const updated = existing
          ? prev.map((conv) => (conv.id === conversation.id ? conversation : conv))
          : [...prev, conversation];

        return updated.sort((a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });

      return conversation;
    } catch (error) {
      console.error('Error loading club conversation:', error);
      throw error;
    }
  };

  const markAsRead = async (conversationId: string) => {
    try {
      const token = await getAuthToken();
      
      await fetch(`${apiBaseUrl}/api/chats/conversations/${conversationId}/read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update local state
      setConversations((prev) => {
        const updated = prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        
        // Recalculate total unread chats
        const unreadCount = updated.reduce((total, conv) => {
          return total + (conv.unreadCount > 0 ? 1 : 0);
        }, 0);
        setTotalUnreadChats(unreadCount);
        console.log('💬 Total unread chats after marking as read:', unreadCount);
        
        return updated;
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const refreshConversations = async () => {
    await fetchConversations(0);
  };

  const refreshMessages = async (conversationId: string) => {
    await fetchMessages(conversationId, 0);
  };

  const clearMessages = () => {
    console.log('🧹 Clearing messages');
    setCurrentMessages([]);
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        currentMessages,
        loading,
        totalUnreadChats,
        fetchConversations,
        fetchMessages,
        sendMessage,
        createConversation,
        markAsRead,
        refreshConversations,
        refreshMessages,
        clearMessages,
        getClubConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
